import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '@/components/ui/PageHeader';
import { Spinner } from '@/components/ui/Spinner';
import { PageContainer } from '@/components/ui/PageContainer';
import { ListingBlock } from '@/components/ui';
import { Input } from '@/components/ui/Input/Input';
import { EmptyState } from '@/components/ui/EmptyState/EmptyState';
import { EventsService, type EventWithClientDTO } from '@/services/EventsService';
import { FlightManifestService, type EventManifestSummaryDTO } from '@/services/FlightManifestService';
import { useNotificationStore } from '@/stores/notificationStore';
import { FileText, Search, Users, Plane, Calendar } from 'lucide-react';

const MONTH_ABBR = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'] as const;

const formatEventDates = (start?: string | null, end?: string | null) => {
	const fmt = (d: string) => {
		const dt = new Date(d);
		const dd = String(dt.getDate()).padStart(2, '0');
		const mmm = MONTH_ABBR[dt.getMonth()];
		return `${dd}/${mmm}/${dt.getFullYear()}`;
	};
	if (start && end) return `${fmt(start)} – ${fmt(end)}`;
	if (start) return fmt(start);
	if (end) return fmt(end);
	return null;
};

interface EventWithManifestCounts extends EventWithClientDTO {
	passenger_count: number;
	total_flights: number;
}

export function FlightManifests() {
	const navigate = useNavigate();
	const eventsService = useMemo(() => new EventsService(), []);
	const manifestService = useMemo(() => new FlightManifestService(), []);
	const notifyError = useNotificationStore(s => s.error);

	const [loading, setLoading] = useState(true);
	const [events, setEvents] = useState<EventWithManifestCounts[]>([]);
	const [searchQuery, setSearchQuery] = useState('');

	async function loadEvents() {
		setLoading(true);
		try {
			const [allEvents, manifestSummaries] = await Promise.all([
				eventsService.listAll(),
				manifestService.listEventsWithManifests(),
			]);

			// Build a lookup map from manifest summaries
			const manifestMap = new Map<string, EventManifestSummaryDTO>();
			for (const s of manifestSummaries) {
				manifestMap.set(s.event_id, s);
			}

			// Merge all events with manifest counts
			const merged: EventWithManifestCounts[] = allEvents.map(event => {
				const summary = manifestMap.get(event.id);
				return {
					...event,
					passenger_count: summary?.passenger_count ?? 0,
					total_flights: summary?.total_flights ?? 0,
				};
			});

			setEvents(merged);
		} catch (e: any) {
			notifyError(e?.message || 'Failed to load events');
		} finally {
			setLoading(false);
		}
	}

	useEffect(() => { loadEvents(); }, []);

	const filteredEvents = events.filter(event =>
		event.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
		event.client_name.toLowerCase().includes(searchQuery.toLowerCase())
	);

	return (
		<PageContainer>
			<PageHeader
				title="Flight Manifests"
				description="Review and manage passenger flight data across events"
			/>

			{loading ? (
				<div className="flex justify-center py-12"><Spinner /></div>
			) : events.length === 0 ? (
				<div className="max-w-2xl mx-auto px-6 py-12">
					<EmptyState
						icon={<FileText className="w-12 h-12" />}
						title="No Events Found"
						description="Create an event for a client first, then you can manage flight manifests."
					/>
				</div>
			) : (
				<div className="max-w-4xl mx-auto">
					<div className="mb-6">
						<Input
							placeholder="Search events by name or client..."
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
							leftIcon={<Search className="w-4 h-4" />}
						/>
					</div>

					<div className="space-y-3">
						{filteredEvents.length === 0 ? (
							<EmptyState
								icon={<Search className="w-8 h-8" />}
								title="No matching events"
								description="Try adjusting your search query"
							/>
						) : (
							filteredEvents.map((event) => {
								const dateStr = formatEventDates(event.start_date, event.end_date);
								const metaItems = [];

								if (dateStr) {
									metaItems.push({
										icon: <Calendar className="w-4 h-4 text-primary-600" />,
										label: 'Dates',
										value: dateStr,
									});
								}
								metaItems.push({
									icon: <Users className="w-4 h-4 text-primary-600" />,
									label: 'Passengers',
									value: event.passenger_count.toString(),
								});
								metaItems.push({
									icon: <Plane className="w-4 h-4 text-primary-600" />,
									label: 'Flights',
									value: event.total_flights.toString(),
								});

								return (
									<ListingBlock
										key={event.id}
										title={event.name}
										subtitle={`Client: ${event.client_name}`}
										metaItems={metaItems}
										onClick={() => navigate(`/tools/flight-manifests/${event.id}`)}
									/>
								);
							})
						)}
					</div>
				</div>
			)}
		</PageContainer>
	);
}
